// SD23/FR-P3b and SD25/FR-P12: the watch page's two on-demand media jobs — the quality ladder
// (`POST /grackles/{id}/transcode`) and the deferred remux (`POST /grackles/{id}/remux`) — and
// the client state that makes them visible from "asked" to "done" or "failed".
//
// Why this exists: the ladder button used to flip to "Building…" and straight back, because the
// "has it arrived?" check was the remux's (`!needsRemux`), which is already true for any grackle
// that has segments — i.e. every grackle the ladder button is shown for. So the poll never
// started, the label reverted on the next render, and a dead ladder job (which by design leaves
// the grackle row untouched, workers/src/transcode.rs `mark_failed`) was never reported.
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiFail } from "../api/client";
import type { PublishStatus, WatchOut } from "../api/types";

export type MediaJobKind = "remux" | "transcode";

/** Where a requested job is, as far as the client can tell. */
export type MediaJobPhase =
  | { state: "idle" }
  | { state: "starting" }
  | { state: "queued" }
  | { state: "running" }
  /** A failed attempt that the job runner will retry with backoff (jobs.rs `fail`). */
  | { state: "retrying"; attempt: number; error: string | null }
  | { state: "done" }
  /** Dead-lettered, or the request itself was refused (403 permission, validation…). */
  | { state: "failed"; error: string }
  /** Still not finished after `SLOW_AFTER_MS` — stop polling rather than spin forever. */
  | { state: "slow" };

const POLL_MS = 5000;
/** A long recording's full ladder can legitimately take a while; past this, stop polling and
 * say so honestly instead of showing a spinner indefinitely. */
const SLOW_AFTER_MS = 30 * 60 * 1000;

/** §2.9 rung selection, mirrored from workers/src/ffmpeg.rs `select_rungs` so the dialog can say
 * exactly which levels will be produced: every ladder rung at or below the source height, never
 * an upscale; a source shorter than 360px gets a single rung at its own height. */
const LADDER_HEIGHTS = [1080, 720, 480, 360] as const;
export function ladderRungsFor(sourceHeight: number | null): string[] | null {
  if (!sourceHeight || sourceHeight <= 0) return null;
  if (sourceHeight < 360) return [`${sourceHeight}p`];
  return LADDER_HEIGHTS.filter((h) => h <= sourceHeight).map((h) => `${h}p`);
}

/** Has the job's output reached the watch payload? */
export function jobArrived(kind: MediaJobKind, watch: WatchOut): boolean {
  if (kind === "transcode") return watch.renditions.some((r) => r.name !== "source");
  return watch.renditions.length > 0;
}

/** Map the latest `transcode` job row (publish-status) to a phase; null when it says nothing new. */
export function phaseFromJob(job: PublishStatus["transcode_job"]): MediaJobPhase | null {
  if (!job) return null;
  switch (job.status) {
    case "queued":
      return { state: "queued" };
    case "running":
      return { state: "running" };
    case "failed":
      return { state: "retrying", attempt: job.attempts, error: job.last_error };
    case "dead":
      return { state: "failed", error: job.last_error ?? "The job stopped without reporting why." };
    case "done":
      return { state: "done" };
    default:
      return null;
  }
}

function isActive(phase: MediaJobPhase): boolean {
  return (
    phase.state === "starting" || phase.state === "queued" || phase.state === "running" || phase.state === "retrying"
  );
}

function requestError(err: unknown): string {
  return err instanceof ApiFail ? err.message : "Couldn't reach Grackle. Check your connection.";
}

/**
 * Tracks one on-demand media job for the watch page. `reload` re-fetches the watch payload; the
 * hook polls it (so new renditions and the quality menu appear without a manual reload) and,
 * where the caller is allowed to read it, `GET /uploads/{id}/publish-status` for the job row —
 * the only place a ladder failure is recorded. That endpoint is owner-only; for an admin editing
 * someone else's recording it 404s and the hook falls back to watching for the renditions alone.
 */
export function useMediaJob(grackleId: string | undefined, watch: WatchOut | null, reload: () => Promise<void>) {
  const [kind, setKind] = useState<MediaJobKind | null>(null);
  const [phase, setPhase] = useState<MediaJobPhase>({ state: "idle" });
  const startedAt = useRef(0);
  const canReadJob = useRef(true);

  const active = isActive(phase);

  // A different recording starts from a clean slate.
  useEffect(() => {
    setKind(null);
    setPhase({ state: "idle" });
  }, [grackleId]);

  // Output landed in the watch payload: that is success regardless of what the job row says.
  useEffect(() => {
    if (!kind || !watch || !active || phase.state === "starting") return;
    if (jobArrived(kind, watch)) setPhase({ state: "done" });
  }, [kind, watch, active, phase.state]);

  const poll = useCallback(async () => {
    if (!grackleId) return;
    if (Date.now() - startedAt.current > SLOW_AFTER_MS) {
      setPhase((prev) => (isActive(prev) ? { state: "slow" } : prev));
      return;
    }
    await reload();
    if (!canReadJob.current) return;
    try {
      const status = await api<PublishStatus>("GET", `/uploads/${grackleId}/publish-status`);
      const next = phaseFromJob(status.transcode_job);
      if (!next) return;
      if (next.state === "done") {
        // The job finished; one more reload brings in its renditions (the effect above then
        // confirms). Report done either way — the worker commits renditions before `done`.
        await reload();
      }
      // An answer that lands after the user dismissed the notice (or after arrival) is stale.
      setPhase((prev) => (isActive(prev) ? next : prev));
    } catch (err) {
      // Owner-only endpoint: for anyone else, watch the renditions alone. A transient failure
      // just waits for the next tick.
      if (err instanceof ApiFail && (err.status === 404 || err.status === 403)) canReadJob.current = false;
    }
  }, [grackleId, reload]);

  useEffect(() => {
    if (!active || phase.state === "starting") return;
    const id = setInterval(() => void poll(), POLL_MS);
    return () => clearInterval(id);
  }, [active, phase.state, poll]);

  const start = useCallback(
    async (nextKind: MediaJobKind) => {
      if (!grackleId) return;
      setKind(nextKind);
      setPhase({ state: "starting" });
      startedAt.current = Date.now();
      canReadJob.current = true;
      try {
        const res = await api<{ status: string }>("POST", `/grackles/${grackleId}/${nextKind}`);
        if (res.status === "already_done") {
          await reload();
          setPhase({ state: "done" });
          return;
        }
        setPhase(res.status === "already_in_progress" ? { state: "running" } : { state: "queued" });
      } catch (err) {
        setPhase({ state: "failed", error: requestError(err) });
      }
    },
    [grackleId, reload],
  );

  const dismiss = useCallback(() => {
    setPhase({ state: "idle" });
    setKind(null);
  }, []);

  return { kind, phase, busy: active, start, dismiss };
}
